import { Controller, type Control } from 'react-hook-form'
import { Loader2, AlertCircle, Home, Check, MapPin, FileText, Mail, Map } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { SectionCard } from '@/components/ui/SectionCard'
import { CompanyInfoAddressValidationBanner } from '@/features/company/components/edit-tabs/companyInfo/CompanyInfoAddressValidationBanner'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import { cn } from '@/lib/utils/helpers'
import { parseApiTimestampToDate } from '@/lib/utils/parseApiTimestamp'
import { maskPhoneOnChange, PROPERTY_PHONE_INPUT_MAX_LENGTH } from '../utils/formatPhone'
import { getW9FileId, getW9FileName } from '../utils/w9Files'
import type { PropertyEditInfoTabProps, PropertyEditFormValues } from '../types'
import { useUserRole } from '@/hooks/useUserRole'

function StatusHeaderToggle({
  control,
  canEditListed,
}: {
  control: Control<PropertyEditFormValues>
  canEditListed: boolean
}) {
  return (
    <Controller
      name="listed"
      control={control}
      render={({ field }) => {
        const isActive = String(field.value ?? '1') === '1'
        const handleToggle = (targetValue: '1' | '0') => {
          if (!canEditListed || String(field.value) === targetValue) return
          field.onChange(targetValue)
        }
        return (
          <div className="flex items-center gap-2" data-testid="property-edit-status-header-container">
            <span className="text-sm font-bold text-slate-800">Status</span>
            <div
              className={cn(
                'inline-flex items-center rounded-lg bg-slate-100 p-1 border border-slate-200/80 text-xs font-medium',
                !canEditListed && 'opacity-60 cursor-not-allowed',
              )}
              data-testid="property-edit-status-toggle-group"
            >
              <button
                type="button"
                disabled={!canEditListed}
                onClick={() => handleToggle('1')}
                data-testid="property-edit-status-active-toggle"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                  isActive
                    ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white font-semibold border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                    : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50',
                )}
              >
                {isActive && <Check className="h-3.5 w-3.5" />}
                <span>Active</span>
              </button>
              <button
                type="button"
                disabled={!canEditListed}
                onClick={() => handleToggle('0')}
                data-testid="property-edit-status-inactive-toggle"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                  !isActive
                    ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white font-semibold border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                    : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50',
                )}
              >
                {!isActive && <Check className="h-3.5 w-3.5" />}
                <span>Not active</span>
              </button>
            </div>
          </div>
        )
      }}
    />
  )
}

export function PropertyEditInfoTab({
  register,
  control,
  errors,
  watchIsMailingAddress,
  states,
  companies,
  canEditPropertyInfo,
  canEditListed,
  isLibertyRole,
  isCompanySelectionLocked,
  propertyDetails,
  propertyId,
  loadCompanies,
  addressInputRef,
  streetAddressRef,
  enableSetAddress,
  showGoogleAddress,
  streetAddress,
  showLatLongLookup,
  showMap,
  isMapMessage,
  mapRef,
  refreshMap,
  onSetPlaceAddress,
  onAddressNotFound,
  onValidateAddress,
  onLatLongLookup,
  isValidatingAddress,
  mapMessage,
  w9Files,
  w9Uploading,
  w9StagingName,
  w9InputRef,
  onW9Upload,
  onW9Download,
  onW9DeleteRequest,
}: PropertyEditInfoTabProps) {
  const { isPropertyRole } = useUserRole()
  const canUploadW9 = canEditPropertyInfo && !isPropertyRole
  const transferStatus = (propertyDetails?.transfer_log_status as Record<string, unknown> | undefined)?.status
  const propertyAddressField = register('property_address')

  return (
    <>
      <input data-testid="property-edit-units-count-hidden" type="hidden" {...register('units_count')} />
      <input data-testid="property-edit-range-hidden" type="hidden" {...register('range')} />

      <SectionCard
        icon={Home}
        title="Property Information"
        testId="property-edit-section-property-information"
        titleTestId="property-edit-section-property-information-title"
        headerActions={<StatusHeaderToggle control={control} canEditListed={canEditListed} />}
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                Select Company
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <Controller
                name="fk_company_id"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    placeholder="Select Company"
                    options={companies}
                    value={field.value ? String(field.value) : ''}
                    onBlur={field.onBlur}
                    onChange={(v) => field.onChange(Array.isArray(v) ? v[0] : v)}
                    onSearch={loadCompanies}
                    disabled={!canEditPropertyInfo || isCompanySelectionLocked}
                    triggerDataTestId="property-edit-company-select"
                  />
                )}
              />
              {errors.fk_company_id && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.fk_company_id.message}
                </div>
              )}
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                Property Name
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                {...register('property_name')}
                data-testid="property-edit-property-name-input"
                className={`${companyFormStyles.customFormControl} ${errors.property_name ? '!border-red-500' : ''}`}
              />
              {errors.property_name && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_name.message}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>Office Phone</label>
              <Controller
                name="property_office_phone"
                control={control}
                render={({ field }) => (
                  <input
                    type="tel"
                    inputMode="tel"
                    maxLength={PROPERTY_PHONE_INPUT_MAX_LENGTH}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(maskPhoneOnChange(e.target.value))}
                    data-testid="property-edit-office-phone-input"
                    className={`${companyFormStyles.customFormControl} ${errors.property_office_phone ? '!border-red-500' : ''}`}
                    placeholder="(xxx) xxx-xxxx"
                  />
                )}
              />
              {errors.property_office_phone && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_office_phone.message}
                </div>
              )}
            </div>
            {isLibertyRole && (
              <div className={companyFormStyles.formGroup}>
                <label className={companyFormStyles.formLabel}>Property Alias Name</label>
                <input
                  type="text"
                  {...register('property_alias_name')}
                  data-testid="property-edit-alias-name-input"
                  className={companyFormStyles.customFormControl}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>Primary Contact</label>
              <input
                type="text"
                {...register('contact_name')}
                data-testid="property-edit-contact-name-input"
                className={`${companyFormStyles.customFormControl} ${errors.contact_name ? '!border-red-500' : ''}`}
              />
              {errors.contact_name && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.contact_name.message}
                </div>
              )}
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>Primary Phone</label>
              <Controller
                name="contact_phone"
                control={control}
                render={({ field }) => (
                  <input
                    type="tel"
                    inputMode="tel"
                    maxLength={PROPERTY_PHONE_INPUT_MAX_LENGTH}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(maskPhoneOnChange(e.target.value))}
                    data-testid="property-edit-contact-phone-input"
                    className={`${companyFormStyles.customFormControl} ${errors.contact_phone ? '!border-red-500' : ''}`}
                    placeholder="(xxx) xxx-xxxx"
                  />
                )}
              />
              {errors.contact_phone && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.contact_phone.message}
                </div>
              )}
            </div>
          </div>

          <div className="text-[13px] text-[#778aa4]" data-testid="property-edit-transfer-status">
            <span>Transfer Status : </span>
            <span
              data-testid="property-edit-transfer-status-value"
              className={cn(
                transferStatus === 'Pending' && 'font-semibold text-[#e67e22]',
                transferStatus !== 'Pending' && 'font-normal',
              )}
            >
              {(() => {
                const tls = propertyDetails?.transfer_log_status as Record<string, unknown> | undefined
                if (tls?.status === 'Completed') {
                  const d = parseApiTimestampToDate(tls.date)
                  return `Transferred ${
                    d
                      ? `${d.toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        })} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
                      : ''
                  }`
                }
                if (tls?.status === 'Pending') return 'Transfer Pending'
                return 'No Transfer History'
              })()}
            </span>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={MapPin}
        title="Physical Address"
        testId="property-edit-section-physical-address"
        titleTestId="property-edit-section-physical-address-title"
      >
        <div className="space-y-6">
          <CompanyInfoAddressValidationBanner
            message={mapMessage}
            testId="property-edit-address-validation-message"
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                Address Line1
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <div className="flex items-start gap-2">
                <textarea
                  rows={2}
                  {...propertyAddressField}
                  ref={(el) => {
                    propertyAddressField.ref(el)
                    addressInputRef.current = el
                  }}
                  data-testid="property-edit-address-line1-input"
                  className={`min-h-[72px] flex-1 resize-y ${companyFormStyles.customFormControl} ${errors.property_address ? '!border-red-500' : ''}`}
                />
              </div>
              {errors.property_address && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_address.message}
                </div>
              )}
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>Address Line2</label>
              <textarea
                rows={2}
                {...register('property_address2')}
                data-testid="property-edit-address-line2-input"
                className={`min-h-[72px] resize-y ${companyFormStyles.customFormControl}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                City
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                {...register('property_city')}
                data-testid="property-edit-city-input"
                className={`${companyFormStyles.customFormControl} ${errors.property_city ? '!border-red-500' : ''}`}
              />
              {errors.property_city && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_city.message}
                </div>
              )}
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                Select State
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <div className={companyFormStyles.selectWrap}>
                <select
                  {...register('property_state')}
                  data-testid="property-edit-state-select"
                  className={`${companyFormStyles.customFormControl} ${errors.property_state ? '!border-red-500' : ''}`}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.state_code} value={s.state_code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.property_state && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_state.message}
                </div>
              )}
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel}>
                Zipcode
                <span className={companyFormStyles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                {...register('property_zipcode')}
                data-testid="property-edit-zipcode-input"
                className={`${companyFormStyles.customFormControl} ${errors.property_zipcode ? '!border-red-500' : ''}`}
              />
              {errors.property_zipcode && (
                <div className={companyFormStyles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errors.property_zipcode.message}
                </div>
              )}
            </div>
          </div>

          {canEditPropertyInfo && (
            <div className="space-y-3 pt-1">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={onValidateAddress}
                  data-testid="property-edit-validate-address-button"
                  className={companyFormStyles.btnPrimary}
                >
                  Validate Address
                </button>
                {showLatLongLookup && (
                  <button
                    type="button"
                    onClick={() => void onLatLongLookup()}
                    disabled={isValidatingAddress}
                    data-testid="property-edit-lat-long-lookup-button"
                    className={companyFormStyles.btnPrimary}
                  >
                    {isValidatingAddress && <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />}
                    {isValidatingAddress ? 'Validating...' : 'Lat/Long Lookup'}
                  </button>
                )}
                <label
                  htmlFor="is_mailing_address"
                  className="inline-flex cursor-pointer items-center gap-2 text-sm text-[#253c5c]"
                >
                  <input
                    type="checkbox"
                    id="is_mailing_address"
                    {...register('is_mailing_address')}
                    data-testid="property-edit-mailing-address-checkbox"
                    disabled={!canEditPropertyInfo}
                    className="h-4 w-4 cursor-pointer"
                  />
                  Different Mailing Address?
                </label>
              </div>
              {showGoogleAddress && (
                <>
                  <div className="max-w-xl">
                    <input
                      key="property-street-autocomplete"
                      ref={streetAddressRef}
                      type="text"
                      defaultValue={streetAddress}
                      placeholder="Enter your address"
                      data-testid="property-edit-street-address-input"
                      className={companyFormStyles.customFormControl}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      data-testid="property-edit-set-address-button"
                      onClick={onSetPlaceAddress}
                      disabled={!enableSetAddress}
                      className={companyFormStyles.btnPrimary}
                    >
                      Set Address
                    </button>
                    <button
                      type="button"
                      data-testid="property-edit-address-not-found-button"
                      onClick={onAddressNotFound}
                      className={companyFormStyles.btnPrimary}
                    >
                      Not Found
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </SectionCard>

      {watchIsMailingAddress && (
        <div
          id="property-edit-mailing-address-section"
          data-testid="property-edit-mailing-address-section"
        >
          <SectionCard icon={Mail} title="Mailing Address">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>
                    Address Line1<span className={companyFormStyles.requiredStar}>*</span>
                  </label>
                  <textarea
                    rows={2}
                    {...register('mailing_property_address')}
                    data-testid="property-edit-mailing-address-line1-input"
                    disabled={!canEditPropertyInfo}
                    className={cn(
                      companyFormStyles.customFormControl,
                      'block min-h-[72px] w-full resize-y',
                      errors.mailing_property_address && '!border-red-500',
                    )}
                  />
                  {errors.mailing_property_address && (
                    <div className={companyFormStyles.errorMessage}>
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {errors.mailing_property_address.message}
                    </div>
                  )}
                </div>
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>Address Line2</label>
                  <textarea
                    rows={2}
                    {...register('mailing_property_address2')}
                    data-testid="property-edit-mailing-address-line2-input"
                    disabled={!canEditPropertyInfo}
                    className={cn(
                      companyFormStyles.customFormControl,
                      'block min-h-[72px] w-full resize-y',
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>
                    City<span className={companyFormStyles.requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    {...register('mailing_property_city')}
                    data-testid="property-edit-mailing-city-input"
                    disabled={!canEditPropertyInfo}
                    className={cn(
                      companyFormStyles.customFormControl,
                      errors.mailing_property_city && '!border-red-500',
                    )}
                  />
                  {errors.mailing_property_city && (
                    <div className={companyFormStyles.errorMessage}>
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {errors.mailing_property_city.message}
                    </div>
                  )}
                </div>
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>
                    Select State<span className={companyFormStyles.requiredStar}>*</span>
                  </label>
                  <div className={companyFormStyles.selectWrap}>
                    <select
                      {...register('mailing_property_state')}
                      data-testid="property-edit-mailing-state-select"
                      disabled={!canEditPropertyInfo}
                      className={cn(
                        companyFormStyles.customFormControl,
                        errors.mailing_property_state && '!border-red-500',
                      )}
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.state_code} value={s.state_code}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.mailing_property_state && (
                    <div className={companyFormStyles.errorMessage}>
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {errors.mailing_property_state.message}
                    </div>
                  )}
                </div>
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>
                    Zipcode<span className={companyFormStyles.requiredStar}>*</span>
                  </label>
                  <input
                    type="text"
                    {...register('mailing_property_zipcode')}
                    data-testid="property-edit-mailing-zipcode-input"
                    disabled={!canEditPropertyInfo}
                    className={cn(
                      companyFormStyles.customFormControl,
                      errors.mailing_property_zipcode && '!border-red-500',
                    )}
                  />
                  {errors.mailing_property_zipcode && (
                    <div className={companyFormStyles.errorMessage}>
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {errors.mailing_property_zipcode.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      <SectionCard
        icon={FileText}
        title="Federal ID"
        testId="property-edit-section-federal-id"
        titleTestId="property-edit-section-federal-id-title"
      >
        <div className="flex flex-wrap items-start gap-6">
          <div className={cn(companyFormStyles.formGroup, companyFormStyles.w9FieldGroupSm)}>
            <label className={companyFormStyles.formLabel}>Federal ID Number</label>
            <input
              type="text"
              {...register('federal_id')}
              data-testid="property-edit-federal-id-input"
              className={companyFormStyles.customFormControl}
              placeholder="XX-XXXXXXX"
            />
          </div>
          <div className={cn(companyFormStyles.formGroup, companyFormStyles.w9FieldGroupMd)}>
            <label className={companyFormStyles.formLabel}>W-9 File</label>
            {propertyId && (
              <div
                className={companyFormStyles.w9UploadRow}
                style={{
                  opacity: !canUploadW9 ? 0.6 : 1,
                  cursor: !canUploadW9 ? "not-allowed" : "pointer"
                }}
              >
                <input
                  ref={w9InputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  data-testid="property-edit-w9-file-input"
                  className={companyFormStyles.w9HiddenInput}
                  disabled={w9Uploading || !canUploadW9}
                  onChange={(e) => {
                    if (canUploadW9) {
                      onW9Upload(e.target.files)
                    }
                  }}
                />
                <input
                  type="text"
                  readOnly
                  placeholder={!canUploadW9 ? "Upload disabled" : "Choose file..."}
                  value={w9StagingName}
                  data-testid="property-edit-w9-filename-display-input"
                  className={cn(companyFormStyles.customFormControl, companyFormStyles.w9FilenameInput)}
                  style={{
                    cursor: !canUploadW9 ? "not-allowed" : "pointer"
                  }}
                  onClick={() => canUploadW9 && w9InputRef.current?.click()}
                />
                <button
                  type="button"
                  data-testid="property-edit-w9-upload-button"
                  disabled={w9Uploading || !canUploadW9}
                  className={cn(companyFormStyles.w9UploadButton, w9Uploading && companyFormStyles.w9UploadButtonBusy)}
                  style={{
                    cursor: (!canUploadW9 || w9Uploading) ? "not-allowed" : "pointer"
                  }}
                  onClick={() => canUploadW9 && w9InputRef.current?.click()}
                >
                  {w9Uploading ? 'Uploading…' : 'Upload'}
                </button>
              </div>
            )}
            {w9Files.length > 0 && (
              <div>
                {w9Files.map((file, idx) => {
                  const fid = getW9FileId(file)
                  if (!fid) return null
                  return (
                    <div
                      key={`${fid}-${idx}`}
                      className={companyFormStyles.w9FileRow}
                    >
                      <button
                        type="button"
                        data-testid={`property-edit-w9-download-${fid}-button`}
                        onClick={() => onW9Download(fid)}
                        className={companyFormStyles.w9FileLink}
                      >
                        {getW9FileName(file, idx)}
                      </button>
                      {canUploadW9 && (
                        <button
                          type="button"
                          title="Delete"
                          data-testid={`property-edit-w9-remove-${fid}-button`}
                          onClick={() => onW9DeleteRequest(fid)}
                          className={companyFormStyles.w9DeleteButton}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <input type="hidden" {...register('website_url')} data-testid="property-edit-website-input" />
      </SectionCard>

      {showMap && (
        <SectionCard
          icon={Map}
          title="Location Map"
          testId="property-edit-latlong-map-section"
        >
          <div className="mb-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel} htmlFor="property-edit-latitude-input">
                Latitude
              </label>
              <input
                id="property-edit-latitude-input"
                type="number"
                step="any"
                readOnly={!isMapMessage}
                data-testid="property-edit-latitude-input"
                {...register('latitude')}
                className={companyFormStyles.customFormControl}
              />
            </div>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel} htmlFor="property-edit-longitude-input">
                Longitude
              </label>
              <input
                id="property-edit-longitude-input"
                type="number"
                step="any"
                readOnly={!isMapMessage}
                data-testid="property-edit-longitude-input"
                {...register('longitude')}
                className={companyFormStyles.customFormControl}
              />
            </div>
            {canEditPropertyInfo && (
              <div className="flex items-end">
                <button
                  type="button"
                  data-testid="property-edit-refresh-map-button"
                  className={companyFormStyles.btnPrimary}
                  disabled={isValidatingAddress}
                  onClick={refreshMap}
                >
                  Refresh Map
                </button>
              </div>
            )}
          </div>
          <div
            data-testid="property-edit-map-container"
            className="h-[400px] w-full overflow-hidden rounded-lg border border-slate-200"
          >
            <div ref={mapRef} className="h-full w-full" />
          </div>
        </SectionCard>
      )}
    </>
  )
}
